import React, { type PropsWithChildren, type ComponentPropsWithoutRef } from 'react';
import castArray from 'lodash/castArray';
import { Box, xcss, media, type XCSS } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import {
	CellHoverProvider,
	useCellHoverHandlers,
} from '@atlassian/jira-hover-popover/src/controllers/index.tsx';
import { getColumnOffset } from '../../../ui/issue-table/utils.tsx';
import {
	useIsConditionalFormattingEnabled,
	useIsGridNavigationEnabled,
	useIsSingleLineRowHeightEnabled,
	useIsTimelineEnabled,
	useIsAutoUnstickyOnSmallScreenEnabled,
} from '../../../controllers/features/selectors.tsx';
import { useEventDelegatorRef } from '../../../controllers/use-event-delegator/index.tsx';
import { ROW_HEIGHT, Z_INDEX_STICKY_COLUMN } from '../../constants.tsx';

export type Props = Omit<
	PropsWithChildren<{
		/**
		 * Optional border styles to apply to the `td` element. If not provided, no borders will be rendered.
		 */
		border?: XCSS | Array<XCSS | false | undefined>;
		/**
		 * Optional spacing styles to apply to the `td` element. If not provided, default spacing will apply.
		 */
		spacing?: XCSS | Array<XCSS | false | undefined>;
		formatting?: XCSS | Array<XCSS | false | undefined>;
	}> &
		ComponentPropsWithoutRef<'td'>,
	'onMouseEnter' | 'onMouseLeave' | 'onBlur'
> & {
	isColumnSticky?: boolean;
	stickyOffsets?: number;
	isColumnResizing?: boolean;
	hasRightShadow?: boolean;
	fieldId?: string;
	onFocusHandler?: () => void;
	onBlurHandler?: () => void;
};

export const TableCell = (props: Props) => {
	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
	return isSingleLineRowHeightEnabled &&
		!fg('issue_table_single_line_row_height_event_delegator') ? (
		<CellHoverProvider>
			<TableCellInner {...props} />
		</CellHoverProvider>
	) : (
		<TableCellInner {...props} />
	);
};

const TableCellInner = ({
	children,
	border,
	spacing,
	formatting,
	isColumnSticky,
	stickyOffsets,
	isColumnResizing,
	hasRightShadow,
	fieldId,
	onFocusHandler,
	onBlurHandler,
	...htmlAttributes
}: Props) => {
	const spacingStyles =
		spacing !== undefined
			? castArray(spacing)
			: [fg('nin_issue_table_rtl_fix') ? defaultSpacingStyles : defaultSpacingStylesOld];
	const borderStyles = border !== undefined ? castArray(border) : [];
	const formattingStyles = formatting !== undefined ? castArray(formatting) : [];

	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
	const isTimelineEnabled = useIsTimelineEnabled();

	const eventDelegatorRef =
		isSingleLineRowHeightEnabled && fg('issue_table_single_line_row_height_event_delegator')
			? // eslint-disable-next-line react-hooks/react-compiler
				// eslint-disable-next-line react-hooks/rules-of-hooks
				useEventDelegatorRef(fieldId)
			: null;

	let singleLineRowHeightProps = {};

	if (isSingleLineRowHeightEnabled && !fg('issue_table_single_line_row_height_event_delegator')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { handleMouseEnter, handleMouseLeave } = useCellHoverHandlers();
		singleLineRowHeightProps = {
			onMouseEnter: handleMouseEnter,
			onMouseLeave: handleMouseLeave,
		};
	}

	let isAutoUnstickyOnSmallScreenEnabled = false;
	if (fg('jira_list_frozen_column_bugfix')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isAutoUnstickyOnSmallScreenEnabled = useIsAutoUnstickyOnSmallScreenEnabled();
	}

	const isGridNavigationEnabled = useIsGridNavigationEnabled();
	const isConditionalFormattingEnabled = useIsConditionalFormattingEnabled();
	const stickyColumnZIndex =
		stickyOffsets === 0 ? Z_INDEX_STICKY_COLUMN + 1 : Z_INDEX_STICKY_COLUMN;
	const stickyResponsiveStylesFG = fg('nin_issue_table_rtl_fix')
		? stickyResponsiveStyles
		: stickyResponsiveStylesOld;
	const shadowResponsiveStylesFG = fg('nin_issue_table_rtl_fix')
		? shadowResponsiveStyles
		: shadowResponsiveStylesOld;
	const shadowStylesFG = fg('nin_issue_table_rtl_fix') ? shadowStylesNew : shadowStyles;
	return (
		<Box
			as="td"
			xcss={[
				baseStyles,
				fg('nin_issue_table_rtl_fix') && textAlignInherit,
				...spacingStyles,
				...borderStyles,
				...(isConditionalFormattingEnabled ? formattingStyles : []),
				isGridNavigationEnabled && keyboardNavigationStyles,
				// eslint-disable-next-line no-nested-ternary
				...(isColumnSticky && fg('issue_table_frozen_columns')
					? fg('jira_list_frozen_column_bugfix')
						? [isAutoUnstickyOnSmallScreenEnabled ? stickyResponsiveStylesFG : stickyStyles]
						: [stickyStylesOld]
					: []),
				// eslint-disable-next-line no-nested-ternary
				...(hasRightShadow && fg('issue_table_frozen_columns')
					? fg('jira_list_frozen_column_bugfix')
						? [isAutoUnstickyOnSmallScreenEnabled ? shadowResponsiveStylesFG : shadowStylesFG]
						: [shadowStylesOld]
					: []),
			]}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...htmlAttributes}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(isSingleLineRowHeightEnabled &&
				!fg('issue_table_single_line_row_height_event_delegator') &&
				singleLineRowHeightProps)}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(isGridNavigationEnabled && { tabIndex: -1 })}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(fg('issue_table_frozen_columns') && {
				style: {
					...htmlAttributes.style,
					left: isColumnSticky
						? getColumnOffset(stickyOffsets ?? 0, !!isColumnResizing)
						: undefined,
					// Adjust zIndex for the first sticky column to ensure the inline create indicator line
					// is displayed above it. Previously, zIndex was 7 (greater than CHART_PREVIEW_BAR_Z_INDEX = 6).
					// Now, for the first column (stickyOffsets === 0), we set zIndex to 8.
					// This ensures the indicator line is not hidden behind the second sticky column,
					// whose zIndex remains lower (7).
					zIndex: isColumnSticky ? stickyColumnZIndex : undefined,
					height: isTimelineEnabled ? `${ROW_HEIGHT}px` : undefined,
				},
			})}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(eventDelegatorRef && { ref: eventDelegatorRef })}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(onFocusHandler && fg('jira_list_merged_cell_action_buttons_visibility')
				? { onFocus: onFocusHandler }
				: {})}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(onBlurHandler && fg('jira_list_merged_cell_action_buttons_visibility')
				? { onBlur: onBlurHandler }
				: {})}
		>
			{children}
		</Box>
	);
};

const baseStyles = xcss({
	background: 'inherit',
	// Cross-browser CSS hack to allow inline editable fields to fill the entire space of their `td`. The
	// preferred long-term solution is to adopt a flexbox layout, but until then, please do extensive
	// cross-browser testing before you update this rule.
	height: '100%',
	position: 'relative',
});

const textAlignInherit = xcss({
	textAlign: 'inherit',
});

const keyboardNavigationStyles = xcss({
	position: 'relative',
	':focus': {
		outline: 'unset',
	},
	':focus::before': {
		display: 'block',
		content: '" "',
		position: 'absolute',
		height: '100%',
		width: '100%',
		inset: '0px',
		borderColor: 'color.border.focused',
		borderWidth: 'border.width.focused',
		borderStyle: 'solid',
		boxSizing: 'border-box',
		pointerEvents: 'none',
	},
});

const defaultSpacingStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.100',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':first-of-type': {
		paddingInlineStart: 'space.100',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':last-of-type': {
		paddingInlineEnd: 'space.100',
	},
});

const defaultSpacingStylesOld = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.100',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':first-of-type': {
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		paddingLeft: 'space.100',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':last-of-type': {
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		paddingRight: 'space.100',
	},
});

const stickyStyles = xcss({
	position: 'sticky',
});

const stickyResponsiveStyles = xcss({
	position: 'sticky',
	// @ts-expect-error doesn't support media.md yet
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
	[media.below.md]: { insetInlineStart: '0 !important', position: 'relative' },
});
const stickyResponsiveStylesOld = xcss({
	position: 'sticky',
	// @ts-expect-error doesn't support media.md yet
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/design-system/no-physical-properties
	[media.below.md]: { left: '0 !important', position: 'relative' },
});

const stickyStylesOld = xcss({
	position: 'sticky',
});

const shadowStylesOld = xcss({
	'::after': {
		height: '100%',
		width: '4px',
		content: "''",
		opacity: 0.5,
		transition: 'opacity 0.25s',
		background: 'linear-gradient(90deg, #0000001a 0%, #0000)',
		position: 'absolute',
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		right: '-5px',
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		top: 'space.0',
	},
});
const shadowStylesNew = xcss({
	'::after': {
		height: '100%',
		width: '4px',
		content: "''",
		opacity: 0.5,
		transition: 'opacity 0.25s',
		background: 'linear-gradient(90deg, #0000001a 0%, #0000)',
		position: 'absolute',
		insetInlineEnd: '-5px',
		insetBlockStart: 'space.0',
	},
});

const shadowStyles = xcss({
	'::after': {
		height: '100%',
		width: '4px',
		content: "''",
		opacity: 0.5,
		transition: 'opacity 0.25s',
		background: 'linear-gradient(90deg, #0000001a 0%, #0000)',
		position: 'absolute',
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		right: '-5px',
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		top: 'space.0',
	},
});

const shadowResponsiveStyles = xcss({
	[media.above.md]: {
		'::after': {
			height: '100%',
			width: '4px',
			content: "''",
			opacity: 0.5,
			transition: 'opacity 0.25s',
			background: 'linear-gradient(90deg, #0000001a 0%, #0000)',
			position: 'absolute',
			insetInlineEnd: '-5px',
			insetBlockStart: 'space.0',
		},
	},
});
const shadowResponsiveStylesOld = xcss({
	[media.above.md]: {
		'::after': {
			height: '100%',
			width: '4px',
			content: "''",
			opacity: 0.5,
			transition: 'opacity 0.25s',
			background: 'linear-gradient(90deg, #0000001a 0%, #0000)',
			position: 'absolute',
			// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
			right: '-5px',
			// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
			top: 'space.0',
		},
	},
});
